import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const MarketplaceChannels = () => {
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadChannels();
  }, []);

  const loadChannels = async () => {
    try {
      setLoading(true);
      const response = await api.get('/marketplace/channels');
      setChannels(response.data.channels);
    } catch (err) {
      setError('Failed to load marketplace channels');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async (channelId) => {
    try {
      const response = await api.post(`/marketplace/channels/${channelId}/test`);
      if (response.data.success) {
        alert('Connection successful!');
        loadChannels();
      }
    } catch (err) {
      alert('Connection failed: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async (channelId) => {
    if (!window.confirm('Are you sure you want to delete this channel?')) {
      return;
    }

    try {
      await api.delete(`/marketplace/channels/${channelId}`);
      loadChannels();
    } catch (err) {
      alert('Failed to delete channel: ' + (err.response?.data?.error || err.message));
    }
  };

  const getMarketplaceIcon = (type) => {
    const icons = {
      amazon: '📦',
      aliexpress: '🛒',
      temu: '🏪',
      shein: '👗',
      magento: '🛍️',
      woocommerce: '🔌',
      tiktok: '🎵',
      instagram: '📸',
      facebook: '👍'
    };
    return icons[type] || '🏬';
  };

  const getStatusColor = (status) => {
    const colors = {
      active: '#10b981',
      inactive: '#ef4444',
      pending: '#f59e0b',
      error: '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">
          Home
        </button>
        <span className="breadcrumb-separator">/</span>
        <button onClick={() => navigate('/marketplace')} className="breadcrumb-link">
          Marketplace
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Channels</span>
      </nav>

      {/* Main Card */}
      <div className="card">
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              🔌 Marketplace Channels
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Configure and manage your marketplace integrations
            </p>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/marketplace/channels/new')}
          >
            ➕ Add Channel
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            background: '#fee2e2',
            color: '#dc2626',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: '1px solid #fecaca'
          }}>
            {error}
          </div>
        )}

        {/* Content */}
        {channels.length === 0 ? (
          /* Empty State */
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--gray-600)' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔌</div>
            <h3 style={{ marginBottom: '0.5rem', color: 'var(--gray-900)' }}>No Channels Connected</h3>
            <p style={{ maxWidth: '600px', margin: '0 auto 1.5rem auto' }}>
              Create your first marketplace channel to start selling on multiple platforms.
            </p>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/marketplace/channels/new')}
            >
              ➕ Add Your First Channel
            </button>
          </div>
        ) : (
          /* Channels Grid */
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '1rem'
          }}>
            {channels.map(channel => (
              <div
                key={channel.id}
                style={{
                  padding: '1.5rem',
                  border: `1px solid ${channel.status === 'active' ? 'var(--primary-600)' : 'var(--gray-200)'}`,
                  borderRadius: '8px',
                  backgroundColor: channel.status === 'active' ? '#eff6ff' : 'white',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                      <span style={{ fontSize: '1.5rem' }}>{getMarketplaceIcon(channel.marketplace_type)}</span>
                      <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{channel.name}</h3>
                    </div>
                    <p style={{ margin: '0', fontSize: '0.875rem', color: 'var(--gray-600)', textTransform: 'capitalize' }}>
                      {channel.marketplace_type.replace('_', ' ')}
                    </p>
                  </div>
                  <div style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    backgroundColor: `${getStatusColor(channel.status)}20`,
                    color: getStatusColor(channel.status),
                    textTransform: 'uppercase'
                  }}>
                    {channel.status}
                  </div>
                </div>

                <div style={{ marginBottom: '1rem', fontSize: '0.875rem', color: 'var(--gray-700)' }}>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Auto Publish:</strong> {channel.auto_publish ? '✓ Yes' : '✗ No'}
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Auto Sync:</strong> {channel.auto_sync ? '✓ Yes' : '✗ No'}
                  </div>
                  {channel.last_sync_at && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Last Sync:</strong> {new Date(channel.last_sync_at).toLocaleString()}
                    </div>
                  )}
                  {channel.last_error && (
                    <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                      <strong>Error:</strong> {channel.last_error}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-secondary"
                    style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                    onClick={() => navigate(`/marketplace/channels/${channel.id}/edit`)}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                    onClick={() => handleTest(channel.id)}
                  >
                    🔍 Test
                  </button>
                  <button
                    style={{
                      fontSize: '0.875rem',
                      padding: '0.5rem 1rem',
                      border: '1px solid #dc2626',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      color: '#dc2626',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                    onClick={() => handleDelete(channel.id)}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketplaceChannels;
